<template>
	<div class="w-100 flex-center flex-column px-4">
		<div class="w-100 flex-center flex-column mt-10" style="min-height: 50vh">
			<div class="w-100 flex-center flex-column" style="max-width: 600px">
				<v-alert
					type="error"
					class="mb-8 w-100 text-left rounded"
					variant="tonal"
					density="comfortable"
					v-if="error">
					<span class="w-100 text-left">{{ error }}</span>
				</v-alert>

				<label for="business-desc" class="text-left white--text w-100 mb-1">Business Description</label>
				<v-textarea
					id="business-desc"
					variant="outlined"
					base-color="#fefefe"
					color="#fefefe"
					v-model="businessDescription"
					rows="3"
					bg-color="#0E081850"
					class="mb-1 w-100 dark-input"
					placeholder="Describe your business in a few words"
					density="comfortable"></v-textarea>

				<span class="w-100 text-center small-text white--text white-p" v-if="!businessDescription">
					Start typing to generate name suggestions.
				</span>

				<div class="w-100 flex-center mb-5 flex-column mt-3" v-if="suggestionsLoading" style="max-width: 500px">
					<v-progress-linear indeterminate class="w-100" height="2" color="#958fc5"></v-progress-linear>
					<span class="w-100 text-center white--text white-p small-text mt-4">Loading suggestions...</span>
				</div>

				<div
					class="w-100 flex-center flex-column mb-3"
					style="max-width: 700px"
					v-else-if="businessDescription && !suggestionsLoading">
					<div class="w-100 flex-center flex-column mb-4">
						<div class="w-100 reload-suggestions px-2 pt-1 d-flex align-center justify-space-between">
							<span class="ml-3 smaller-text" style="color: #ffffff90">AI Suggestions</span>
							<v-btn variant="plain" color="#fefefe" class="normal-spacing" @click="getNameSuggestions">
								<v-icon class="mr-1" size="small">mdi-refresh</v-icon>
								<span style="font-size: 12px !important">Reload</span>
							</v-btn>
						</div>
						<div class="w-100" style="display: grid; grid-template-columns: 50% 50%; gap: 0">
							<div
								class="answer-suggestion flex-center pa-4 flex-grow-1 position-relative"
								:class="{
									'suggestion-active': selectedName === item.content,
								}"
								v-for="(item, a) in nameSuggestions"
								:key="`${a}-option`"
								:style="{
									borderBottomRightRadius: a % 2 === 0 ? '0' : '5px',
									borderBottomLeftRadius: a % 2 === 1 ? '0' : '5px',
									borderLeft: a % 2 === 1 ? '1px solid #fefefe20' : 'none',
									borderTop: '1px solid #fefefe20',
								}"
								@click="selectSuggestion(item.content)">
								<span class="w-100 text-center small-text white--text">
									{{ item.content }}
								</span>
							</div>
						</div>
					</div>

					<label for="business-url" class="text-left white--text w-100 mb-2 mt-1">Desired URL</label>
					<div class="w-100 d-flex align-center">
						<v-text-field
							id="business-url"
							v-model="selectedName"
							:error-messages="selectedNameError"
							:class="{ 'error--text': selectedNameError }"
							hint="Check availability and view pricing"
							persistent-hint
							theme="dark"
							variant="outlined"
							:error="domainCheckResult && !domainCheckResult.available"
							class="flex-grow-1 dark-input mr-2"
							color="#fefefe"
							density="comfortable"
							placeholder="Select a suggestion or type your own"
							@input="validateInput"></v-text-field>

						<v-select
							v-model="selectedDomain"
							:items="domainOptions"
							:error="domainCheckResult && !domainCheckResult.available"
							theme="dark"
							label="Domain"
							variant="outlined"
							class="dark-input"
							color="#fefefe"
							density="comfortable"
							style="max-width: 120px"></v-select>
					</div>

					<div v-if="isLoading" class="w-100 text-center flex-center flex-column mt-8 mb-6">
						<v-progress-linear indeterminate class="w-100" height="2" color="#958fc5"></v-progress-linear>
						<span class="w-100 text-center white--text white-p small-text mt-3">
							Checking availability...
						</span>
					</div>

					<div v-if="domainCheckResult && !isLoading" class="w-100 text-center mb-5">
						<div
							v-if="domainCheckResult.available"
							style="background-color: #34b334a2; border-radius: 5px"
							class="d-flex align-center justify-md-space-between justify-start flex-md-row flex-column text-left pa-4 white--text mt-5">
							<span class="w-100 text-left white--text" style="word-break: break-all">
								<v-icon class="mr-2" color="white">mdi-check-circle</v-icon>
								{{ domainCheckResult.domain }} is available.
							</span>

							<div class="flex-center ml-4 mt-md-0 mt-6 mb-md-0 mb-2">
								<img
									src="../../assets/namecheap.png"
									class="hidden-sm-and-down"
									alt="Namecheap"
									style="width: 120px" />
								<img
									src="../../assets/namecheap.webp"
									class="hidden-md-and-up mr-4"
									alt="Namecheap"
									style="width: 50px" />
								<v-btn
									:href="domainCheckResult.ctaLink"
									target="_blank"
									color="#fefefe"
									class="ml-4 w-100 w-md-auto normal-spacing font-weight-regular small-text">
									View Pricing
								</v-btn>
							</div>
						</div>
						<v-alert v-else type="error" class="text-left mt-5" density="comfortable">
							{{ domainCheckResult.domain }} is already taken.
						</v-alert>
					</div>
				</div>
			</div>

			<v-spacer></v-spacer>
			<div class="w-100 plannit-maybe flex-center flex-column py-6 mt-10" style="align-self:">
				<h3 class="white--text font-weight-regular text-h6 text-center">Starting a new business?</h3>

				<a :href="ctaLink" class="blue-text ml-2 text-body mt-1 text-center" target="_blank">
					Generate a full business plan in minutes.
				</a>
			</div>
		</div>
	</div>
</template>

<script>
	import _ from "lodash"
	import axios from "axios"
	export default {
		name: "NameGenerator",
		data: () => ({
			error: null,
			selectedName: null,
			selectedDomain: ".com", // Default domain option
			businessDescription: null,
			nameSuggestions: [],
			suggestionsLoading: false,
			domainOptions: [".com", ".net", ".org", ".io", ".ai"], // Add domain options here
			selectedNameError: null,
			isLoading: false,
			domainCheckResult: null,
			oldNameSuggestions: [],
		}),
		props: {
			ctaLink: {
				type: String,
				required: true,
			},
		},
		mounted() {
			this.getNameSuggestionsDebounced = _.debounce(this.getNameSuggestions, 1000) // Debounced for typing
			this.debouncedCheckDomain = _.debounce(this.checkDomainAvailability, 1000) // Debounce API call
		},
		watch: {
			businessDescription() {
				if (this.businessDescription) {
					this.suggestionsLoading = true
					this.selectedName = null
					this.selectedNameError = null
					this.domainCheckResult = null
					this.getNameSuggestionsDebounced()
				}
			},
			selectedName() {
				if (this.selectedName && !this.selectedNameError) {
					this.isLoading = true
					this.debouncedCheckDomain()
				}
			},
			selectedDomain() {
				if (this.selectedName && !this.selectedNameError) {
					this.isLoading = true
					this.debouncedCheckDomain()
				}
			},
		},
		methods: {
			async checkDomainAvailability() {
				if (!this.selectedName || !this.selectedDomain) return

				const fullDomain = `${this.selectedName}${this.selectedDomain}`
				try {
					const response = await axios.post("/check-domain", { domain: fullDomain })
					this.domainCheckResult = response.data
				} catch (error) {
					console.error("Error checking domain availability:", error)
					this.domainCheckResult = null
					this.error = "Failed to check domain availability."
				} finally {
					this.isLoading = false
				}
			},
			getNameSuggestions() {
				this.suggestionsLoading = true

				let business = {
					name: this.selectedName,
					already_suggested: this.nameSuggestions,
					initial_prompt: this.businessDescription,
				}

				console.log(this.nameSuggestions)

				let payload = {
					business: business,
					question: "What should I name my business?",
					already_suggested: Array.from(
						new Set(
							this.nameSuggestions.length > 0
								? this.nameSuggestions
										.map((s) => s.content)
										.concat(this.oldNameSuggestions.map((s) => s.content))
								: this.oldNameSuggestions.map((s) => s.content),
						),
					),
					name_generator: true,
				}

				axios
					.post("/api/v1/get-suggestions", payload)
					.then((response) => {
						if (response.data.error) {
							this.error = "OpenAI Error: Suggestions not loading"
							this.suggestionsLoading = false
							return
						}

						this.nameSuggestions = response.data.suggestions
						this.oldNameSuggestions.push(...this.nameSuggestions)
						this.suggestionsLoading = false
					})
					.catch((e) => {
						this.error = "Something went wrong. Please try again later."
						this.suggestionsLoading = false
					})
			},
			selectSuggestion(content) {
				this.selectedName = content
					.toLowerCase()
					.replace(/\s+/g, "-") // Replace spaces with hyphens
					.replace(/[^a-z0-9-]/g, "") // Remove non-URL-safe characters
			},
			validateInput(value) {
				const urlSafeRegex = /^[a-z0-9-]+$/
				if (!urlSafeRegex.test(this.selectedName)) {
					this.selectedNameError = "Name contains invalid URL characters."
				} else {
					this.selectedNameError = null
				}
			},
		},
	}
</script>
